import React, { useState, useEffect, useMemo } from 'react';
import HtmlEditor from '../../HtmlEditor';

interface StaticHotel {
    id: string;
    hotelName: string;
    destination: string;
    destinationName: string;
    city: string;
    titleSv: string;
    descriptionSv: string;
    titleEn: string;
    descriptionEn: string;
    translationStatus: 'pending' | 'translated' | 'manual' | 'error';
    isStatic: boolean;
    isActive: boolean;
    starRating?: number;
    tourvisioProductId?: string;
    lastUpdated: string;
}

interface Destination {
    code: string;
    name: string;
    count: number;
    translated: number;
}

interface Props {
    theme?: 'dark' | 'light';
}

const API_BASE = '/api/static-hotels';

const StaticHotelManager: React.FC<Props> = ({ theme = 'dark' }) => {
    const isDark = theme === 'dark';

    // State
    const [hotels, setHotels] = useState<StaticHotel[]>([]);
    const [destinations, setDestinations] = useState<Destination[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedHotel, setSelectedHotel] = useState<StaticHotel | null>(null);
    const [editMode, setEditMode] = useState(false);
    const [translating, setTranslating] = useState(false);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDestination, setSelectedDestination] = useState('');
    const [translationFilter, setTranslationFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Stats
    const [stats, setStats] = useState({ total: 0, translated: 0, pending: 0 });

    // Theme classes
    const bgCard = isDark ? 'bg-slate-800' : 'bg-white';
    const bgInput = isDark ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-300';
    const textPrimary = isDark ? 'text-white' : 'text-gray-900';
    const textSecondary = isDark ? 'text-slate-400' : 'text-gray-500';
    const borderColor = isDark ? 'border-slate-700' : 'border-gray-200';

    // Fetch hotels
    const fetchHotels = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: '30'
            });
            if (selectedDestination) params.append('destination', selectedDestination);
            if (translationFilter) params.append('translationStatus', translationFilter);
            if (searchQuery) params.append('search', searchQuery);

            const res = await fetch(`${API_BASE}?${params}`);
            const data = await res.json();
            if (data.success) {
                setHotels(data.hotels);
                setTotalPages(data.pagination.pages);
            }
        } catch (error) {
            console.error('Failed to fetch hotels:', error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch destinations
    const fetchDestinations = async () => {
        try {
            const res = await fetch(`${API_BASE}/destinations`);
            const data = await res.json();
            if (data.success) {
                setDestinations(data.destinations);
                const total = data.destinations.reduce((sum: number, d: Destination) => sum + d.count, 0);
                const translated = data.destinations.reduce((sum: number, d: Destination) => sum + d.translated, 0);
                setStats({ total, translated, pending: total - translated });
            }
        } catch (error) {
            console.error('Failed to fetch destinations:', error);
        }
    };

    useEffect(() => {
        fetchDestinations();
    }, []);

    useEffect(() => {
        fetchHotels();
    }, [currentPage, selectedDestination, translationFilter, searchQuery]);

    // Update hotel
    const handleUpdateHotel = async (hotelData: Partial<StaticHotel>) => {
        if (!selectedHotel) return;
        try {
            const res = await fetch(`${API_BASE}/${selectedHotel.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(hotelData)
            });
            const data = await res.json();
            if (data.success) {
                setHotels(prev => prev.map(h => h.id === selectedHotel.id ? data.hotel : h));
                setSelectedHotel(data.hotel);
                setEditMode(false);
            }
        } catch (error) {
            console.error('Failed to update hotel:', error);
        }
    };

    // Translate hotel
    const handleTranslate = async (hotelId: string) => {
        setTranslating(true);
        try {
            const res = await fetch(`${API_BASE}/translate/${hotelId}`, { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                setHotels(prev => prev.map(h => h.id === hotelId ? data.hotel : h));
                if (selectedHotel?.id === hotelId) {
                    setSelectedHotel(data.hotel);
                }
            }
        } catch (error) {
            console.error('Failed to translate:', error);
        } finally {
            setTranslating(false);
        }
    };

    // Translation status badge
    const StatusBadge = ({ status }: { status: string }) => {
        const colors: Record<string, string> = {
            translated: 'bg-green-500/20 text-green-400',
            pending: 'bg-yellow-500/20 text-yellow-400',
            manual: 'bg-blue-500/20 text-blue-400',
            error: 'bg-red-500/20 text-red-400'
        };
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-500/20 text-gray-400'}`}>
                {status}
            </span>
        );
    };

    return (
        <div className={`h-full flex flex-col ${isDark ? 'bg-slate-900' : 'bg-gray-100'}`}>
            {/* Header */}
            <div className={`p-6 ${bgCard} border-b ${borderColor}`}>
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className={`text-2xl font-bold ${textPrimary}`}>Static Hotel Content</h1>
                        <p className={textSecondary}>Manage imported hotel descriptions and translations</p>
                    </div>
                    <div className="flex gap-4">
                        <div className={`px-4 py-2 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                            <div className={`text-xs ${textSecondary}`}>Total</div>
                            <div className={`text-xl font-bold ${textPrimary}`}>{stats.total}</div>
                        </div>
                        <div className={`px-4 py-2 rounded-lg bg-green-500/20`}>
                            <div className="text-xs text-green-400">Translated</div>
                            <div className="text-xl font-bold text-green-400">{stats.translated}</div>
                        </div>
                        <div className={`px-4 py-2 rounded-lg bg-yellow-500/20`}>
                            <div className="text-xs text-yellow-400">Pending</div>
                            <div className="text-xl font-bold text-yellow-400">{stats.pending}</div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex gap-4 flex-wrap">
                    <input
                        type="text"
                        placeholder="Search hotels..."
                        value={searchQuery}
                        onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                        className={`px-4 py-2 rounded-lg border ${bgInput} ${textPrimary} w-64`}
                    />
                    <select
                        value={selectedDestination}
                        onChange={e => { setSelectedDestination(e.target.value); setCurrentPage(1); }}
                        className={`px-4 py-2 rounded-lg border ${bgInput} ${textPrimary}`}
                    >
                        <option value="">All Destinations</option>
                        {destinations.map(d => (
                            <option key={d.code} value={d.code}>{d.name} ({d.count})</option>
                        ))}
                    </select>
                    <select
                        value={translationFilter}
                        onChange={e => { setTranslationFilter(e.target.value); setCurrentPage(1); }}
                        className={`px-4 py-2 rounded-lg border ${bgInput} ${textPrimary}`}
                    >
                        <option value="">All Status</option>
                        <option value="translated">Translated</option>
                        <option value="pending">Pending</option>
                        <option value="manual">Manual</option>
                        <option value="error">Error</option>
                    </select>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Hotel List */}
                <div className={`w-1/2 overflow-y-auto border-r ${borderColor}`}>
                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
                        </div>
                    ) : (
                        <div className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}>
                            {hotels.map(hotel => (
                                <div
                                    key={hotel.id}
                                    onClick={() => { setSelectedHotel(hotel); setEditMode(false); }}
                                    className={`p-4 cursor-pointer transition-colors ${selectedHotel?.id === hotel.id
                                        ? 'bg-teal-500/20 border-l-4 border-teal-500'
                                        : `hover:${isDark ? 'bg-slate-800' : 'bg-gray-50'}`
                                        }`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <h3 className={`font-medium ${textPrimary}`}>{hotel.hotelName}</h3>
                                            <p className={`text-sm ${textSecondary}`}>
                                                {hotel.destinationName} {hotel.city && `• ${hotel.city}`}
                                            </p>
                                        </div>
                                        <StatusBadge status={hotel.translationStatus} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className={`p-4 flex justify-center gap-2 border-t ${borderColor}`}>
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className={`px-3 py-1 rounded ${isDark ? 'bg-slate-700' : 'bg-gray-200'} disabled:opacity-50`}
                            >
                                ← Prev
                            </button>
                            <span className={`px-4 py-1 ${textSecondary}`}>
                                {currentPage} / {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className={`px-3 py-1 rounded ${isDark ? 'bg-slate-700' : 'bg-gray-200'} disabled:opacity-50`}
                            >
                                Next →
                            </button>
                        </div>
                    )}
                </div>

                {/* Hotel Detail */}
                <div className={`w-1/2 overflow-y-auto p-6 ${bgCard}`}>
                    {selectedHotel ? (
                        <div>
                            <div className="flex items-center justify-between mb-6">
                                <h2 className={`text-xl font-bold ${textPrimary}`}>{selectedHotel.hotelName}</h2>
                                <div className="flex gap-2">
                                    {selectedHotel.translationStatus !== 'translated' && (
                                        <button
                                            onClick={() => handleTranslate(selectedHotel.id)}
                                            disabled={translating}
                                            className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50"
                                        >
                                            {translating ? '🔄 Translating...' : '🌐 Translate'}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setEditMode(!editMode)}
                                        className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
                                    >
                                        {editMode ? '❌ Cancel' : '✏️ Edit'}
                                    </button>
                                </div>
                            </div>

                            {/* Meta */}
                            <div className={`grid grid-cols-3 gap-4 mb-6 p-4 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                                <div>
                                    <label className={`text-xs ${textSecondary}`}>Destination</label>
                                    <p className={textPrimary}>{selectedHotel.destinationName}</p>
                                </div>
                                <div>
                                    <label className={`text-xs ${textSecondary}`}>City</label>
                                    <p className={textPrimary}>{selectedHotel.city || '-'}</p>
                                </div>
                                <div>
                                    <label className={`text-xs ${textSecondary}`}>Status</label>
                                    <p><StatusBadge status={selectedHotel.translationStatus} /></p>
                                </div>
                            </div>

                            {editMode ? (
                                <EditForm
                                    hotel={selectedHotel}
                                    onSave={handleUpdateHotel}
                                    theme={theme}
                                />
                            ) : (
                                <div className="space-y-6">
                                    {/* Swedish Content */}
                                    <div>
                                        <h3 className={`text-lg font-semibold mb-2 ${textPrimary} flex items-center gap-2`}>
                                            🇸🇪 Swedish
                                        </h3>
                                        <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                                            <h4 className={`font-medium mb-2 ${textPrimary}`}>{selectedHotel.titleSv}</h4>
                                            <p className={`text-sm ${textSecondary} whitespace-pre-wrap`}>
                                                {selectedHotel.descriptionSv || 'No Swedish description'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* English Content */}
                                    <div>
                                        <h3 className={`text-lg font-semibold mb-2 ${textPrimary} flex items-center gap-2`}>
                                            🇬🇧 English
                                        </h3>
                                        <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                                            {selectedHotel.descriptionEn ? (
                                                <>
                                                    <h4 className={`font-medium mb-2 ${textPrimary}`}>{selectedHotel.titleEn}</h4>
                                                    <p className={`text-sm ${textSecondary} whitespace-pre-wrap`}>
                                                        {selectedHotel.descriptionEn}
                                                    </p>
                                                </>
                                            ) : (
                                                <p className={`text-sm italic ${textSecondary}`}>
                                                    No English translation yet. Click "Translate" to generate.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full">
                            <p className={textSecondary}>Select a hotel to view details</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Edit Form Component
const EditForm: React.FC<{
    hotel: StaticHotel;
    onSave: (data: Partial<StaticHotel>) => void;
    theme?: 'dark' | 'light';
}> = ({ hotel, onSave, theme = 'dark' }) => {
    const isDark = theme === 'dark';
    const [formData, setFormData] = useState({
        titleSv: hotel.titleSv || '',
        descriptionSv: hotel.descriptionSv || '',
        titleEn: hotel.titleEn || '',
        descriptionEn: hotel.descriptionEn || '',
        city: hotel.city || '',
        starRating: hotel.starRating || '',
        tourvisioProductId: hotel.tourvisioProductId || ''
    });

    const bgInput = isDark ? 'bg-slate-600 border-slate-500' : 'bg-white border-gray-300';
    const textPrimary = isDark ? 'text-white' : 'text-gray-900';

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>City</label>
                    <input
                        type="text"
                        value={formData.city}
                        onChange={e => setFormData({ ...formData, city: e.target.value })}
                        className={`w-full px-3 py-2 rounded-lg border ${bgInput} ${textPrimary}`}
                    />
                </div>
                <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Star Rating</label>
                    <select
                        value={formData.starRating}
                        onChange={e => setFormData({ ...formData, starRating: e.target.value })}
                        className={`w-full px-3 py-2 rounded-lg border ${bgInput} ${textPrimary}`}
                    >
                        <option value="">Select...</option>
                        {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} Star</option>)}
                    </select>
                </div>
            </div>

            <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Tourvisio Product ID</label>
                <input
                    type="text"
                    value={formData.tourvisioProductId}
                    onChange={e => setFormData({ ...formData, tourvisioProductId: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border ${bgInput} ${textPrimary}`}
                    placeholder="Link to Tourvisio product..."
                />
            </div>

            <div className={`border-t ${isDark ? 'border-slate-600' : 'border-gray-300'} pt-4 mt-4`}>
                <h4 className="font-medium text-blue-400 mb-3">🇸🇪 Swedish Content</h4>
                <div className="space-y-3">
                    <input
                        type="text"
                        value={formData.titleSv}
                        onChange={e => setFormData({ ...formData, titleSv: e.target.value })}
                        placeholder="Swedish Title"
                        className={`w-full px-3 py-2 rounded-lg border ${bgInput} ${textPrimary}`}
                    />
                    <HtmlEditor
                        value={formData.descriptionSv}
                        onChange={html => setFormData({ ...formData, descriptionSv: html })}
                        isDark={isDark}
                    />
                </div>
            </div>

            <div className={`border-t ${isDark ? 'border-slate-600' : 'border-gray-300'} pt-4 mt-4`}>
                <h4 className="font-medium text-green-400 mb-3">🇬🇧 English Content</h4>
                <div className="space-y-3">
                    <input
                        type="text"
                        value={formData.titleEn}
                        onChange={e => setFormData({ ...formData, titleEn: e.target.value })}
                        placeholder="English Title"
                        className={`w-full px-3 py-2 rounded-lg border ${bgInput} ${textPrimary}`}
                    />
                    <HtmlEditor
                        value={formData.descriptionEn}
                        onChange={html => setFormData({ ...formData, descriptionEn: html })}
                        isDark={isDark}
                    />
                </div>
            </div>

            <button
                onClick={() => onSave({
                    ...formData,
                    starRating: formData.starRating ? parseInt(formData.starRating as string) : undefined
                })}
                className="w-full py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 font-medium"
            >
                💾 Save Changes
            </button>
        </div>
    );
};

export default StaticHotelManager;
